import { AwardCriterionType, type EvalCriterionDto } from "../../../autogen/bff-api";

export type EvaluationRow =
  | {
      kind: "group";
      id: string;
      label: string | null;
      type: AwardCriterionType;
      weightPercent: number | null;
      isPrice: boolean;
    }
  | { kind: "criterion"; criterion: EvalCriterionDto; isSub: boolean };

export const isPriceRow = (row: EvaluationRow): boolean =>
  row.kind === "group" ? row.isPrice : row.criterion.isPrice;

const round = (value: number) => Math.round(value * 1e6) / 1e6;

// BFF ScorableCriterion.toScorableRows(): a criterion with sub-criteria gets no row, so its own weight arrives only as parent × own / 100 inside each child.
const groupWeightOf = (children: EvalCriterionDto[]): number | null => {
  for (const { weightPercent: own, effectiveWeightPercent: effective } of children) {
    if (own != null && own !== 0 && effective != null) return round((effective * 100) / own);
  }
  const weights = children
    .map((child) => child.effectiveWeightPercent)
    .filter((weight): weight is number => weight != null);
  return weights.length === 0 ? null : round(weights.reduce((total, weight) => total + weight, 0));
};

const blockKey = (criterion: EvalCriterionDto) => criterion.parentId ?? criterion.id;

const blocksOf = (criteria: EvalCriterionDto[]): EvalCriterionDto[][] => {
  const blocks = new Map<string, EvalCriterionDto[]>();
  for (const criterion of criteria) {
    const key = blockKey(criterion);
    const block = blocks.get(key);
    if (block) block.push(criterion);
    else blocks.set(key, [criterion]);
  }
  return [...blocks.values()];
};

const isPriceBlock = (block: EvalCriterionDto[]) => block.some((criterion) => criterion.isPrice);

const priceFirst = (criteria: EvalCriterionDto[]): EvalCriterionDto[] => {
  const blocks = blocksOf(criteria);
  return [...blocks.filter(isPriceBlock), ...blocks.filter((block) => !isPriceBlock(block))].flat();
};

export const buildEvaluationRows = (criteria: EvalCriterionDto[]): EvaluationRow[] => {
  const ordered = priceFirst(criteria);
  const rows: EvaluationRow[] = [];
  ordered.forEach((criterion, index) => {
    const parentId = criterion.parentId ?? null;
    const startsGroup = parentId != null && parentId !== (ordered[index - 1]?.parentId ?? null);
    if (startsGroup) {
      const children = ordered.filter((row) => row.parentId === parentId);
      rows.push({
        kind: "group",
        id: parentId,
        label: criterion.parentName?.trim() || null,
        type: criterion.type,
        weightPercent: groupWeightOf(children),
        isPrice: isPriceBlock(children),
      });
    }
    rows.push({ kind: "criterion", criterion, isSub: parentId != null });
  });
  return rows;
};
